import { useEffect, useRef, useState, useCallback } from 'react';
import { MessageCircle, X, Send, Sparkles, Loader2 } from 'lucide-react';
import {
  chatSendMessage,
  chatGetOrCreateSession,
  chatListMessages,
} from '@/lib/api';

const STORAGE_KEY = 'pv_chat_session_id';
const OPEN_STORAGE_KEY = 'pv_chat_open';

const WELCOME_MESSAGE = {
  id: 'welcome',
  role: 'assistant',
  content:
    "Hi! I'm PropVeiw's assistant. Ask me anything about your bookings, cleanings, files, or how to use the app — I'll do my best to help.",
};

const SUGGESTIONS_AUTH = [
  'How many bookings do I have this weekend?',
  'How do I add a maintenance task?',
  'Where can I upload house rules?',
];

const SUGGESTIONS_ANON = [
  'What features does PropVeiw have?',
  'How much does the Pro plan cost?',
  'How does the free trial work?',
];

const formatContent = (text) => {
  // Very light markdown → plain HTML: **bold** and preserve newlines.
  if (!text) return null;
  const parts = text.split(/(\*\*[^*]+\*\*)/g);
  return parts.map((chunk, i) => {
    // key blends the index with the chunk content for stability across renders
    const chunkKey = `${i}-${chunk.slice(0, 24)}`;
    if (chunk.startsWith('**') && chunk.endsWith('**')) {
      return (
        <strong key={chunkKey} className="font-semibold text-slate-900">
          {chunk.slice(2, -2)}
        </strong>
      );
    }
    return <span key={chunkKey}>{chunk}</span>;
  });
};

const MessageBubble = ({ role, content }) => {
  const isUser = role === 'user';
  return (
    <div
      className={`flex ${isUser ? 'justify-end' : 'justify-start'}`}
      data-testid={`chat-msg-${role}`}
    >
      <div
        className={`max-w-[85%] rounded-2xl px-3.5 py-2.5 text-sm leading-relaxed whitespace-pre-wrap ${
          isUser
            ? 'bg-[#070d1c] text-white rounded-br-sm'
            : 'bg-slate-100 text-slate-800 rounded-bl-sm'
        }`}
      >
        {formatContent(content)}
      </div>
    </div>
  );
};

export const AIChatWidget = ({ theme = 'light' }) => {
  const [open, setOpen] = useState(false);
  const [messages, setMessages] = useState([WELCOME_MESSAGE]);
  const [input, setInput] = useState('');
  const [sending, setSending] = useState(false);
  const [sessionId, setSessionId] = useState(null);
  const scrollRef = useRef(null);
  const inputRef = useRef(null);

  // Restore open state (helps continuity when navigating)
  useEffect(() => {
    try {
      if (sessionStorage.getItem(OPEN_STORAGE_KEY) === '1') setOpen(true);
    } catch { /* noop */ }
  }, []);

  useEffect(() => {
    try {
      if (open) sessionStorage.setItem(OPEN_STORAGE_KEY, '1');
      else sessionStorage.removeItem(OPEN_STORAGE_KEY);
    } catch { /* noop */ }
  }, [open]);

  // On first open, fetch or create a session and load its history.
  useEffect(() => {
    if (!open || sessionId) return;
    const bootstrap = async () => {
      try {
        // Try server first (returns most-recent session for auth users)
        const { data } = await chatGetOrCreateSession();
        const sid = data.session_id;
        setSessionId(sid);
        try { localStorage.setItem(STORAGE_KEY, sid); } catch { /* noop */ }
        // Load history
        try {
          const { data: hist } = await chatListMessages(sid);
          if (hist?.messages?.length) {
            setMessages([
              WELCOME_MESSAGE,
              ...hist.messages.map((m) => ({
                id: m.id,
                role: m.role,
                content: m.content,
              })),
            ]);
          }
        } catch { /* fresh session, no history */ }
      } catch (err) {
        // Fallback to local storage or generate a temp id on first send
        try {
          const stored = localStorage.getItem(STORAGE_KEY);
          if (stored) setSessionId(stored);
        } catch { /* noop */ }
      }
    };
    bootstrap();
  }, [open, sessionId]);

  // Auto-scroll on new messages
  useEffect(() => {
    if (scrollRef.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }
  }, [messages, sending]);

  // Focus input when opened
  useEffect(() => {
    if (open) {
      setTimeout(() => inputRef.current?.focus(), 100);
    }
  }, [open]);

  const send = useCallback(
    async (raw) => {
      const text = (raw ?? input).trim();
      if (!text || sending) return;
      const userMsg = {
        id: `local-${Date.now()}`,
        role: 'user',
        content: text,
      };
      setMessages((prev) => [...prev, userMsg]);
      setInput('');
      setSending(true);
      try {
        const { data } = await chatSendMessage(text, sessionId);
        if (!sessionId && data.session_id) {
          setSessionId(data.session_id);
          try { localStorage.setItem(STORAGE_KEY, data.session_id); } catch { /* noop */ }
        }
        setMessages((prev) => [
          ...prev,
          { id: data.message_id, role: 'assistant', content: data.reply },
        ]);
      } catch (err) {
        setMessages((prev) => [
          ...prev,
          {
            id: `err-${Date.now()}`,
            role: 'assistant',
            content:
              "Sorry — I ran into a snag reaching my brain. Please try again in a moment.",
          },
        ]);
      } finally {
        setSending(false);
        setTimeout(() => inputRef.current?.focus(), 50);
      }
    },
    [input, sending, sessionId]
  );

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      send();
    }
  };

  const suggestions = sessionId
    ? SUGGESTIONS_AUTH
    : SUGGESTIONS_ANON;
  const showSuggestions = messages.length <= 1;

  // Match brand: gold button on dark; navy on light
  const buttonClass =
    theme === 'dark'
      ? 'bg-amber-400 hover:bg-amber-300 text-[#070d1c] shadow-lg shadow-amber-500/30'
      : 'bg-[#070d1c] hover:bg-[#0c1530] text-amber-300 shadow-lg shadow-amber-500/20 ring-1 ring-amber-300/30';

  return (
    <>
      {/* Floating action button */}
      {!open && (
        <button
          type="button"
          onClick={() => setOpen(true)}
          className={`fixed bottom-20 right-6 z-40 h-14 w-14 rounded-full flex items-center justify-center transition-transform hover:scale-105 ${buttonClass}`}
          aria-label="Open AI assistant"
          data-testid="ai-chat-open-btn"
        >
          <MessageCircle className="h-6 w-6" />
          <span className="absolute -top-1 -right-1 w-3 h-3 rounded-full bg-amber-300 ring-2 ring-[#070d1c] animate-pulse" />
        </button>
      )}

      {/* Chat panel */}
      {open && (
        <div
          className="fixed bottom-20 right-6 z-50 w-[calc(100vw-2rem)] sm:w-[380px] max-h-[calc(100vh-8rem)] flex flex-col bg-white rounded-2xl shadow-2xl ring-1 ring-black/10 overflow-hidden animate-in slide-in-from-bottom-4 duration-200"
          data-testid="ai-chat-panel"
        >
          {/* Header */}
          <div className="flex items-center justify-between px-4 py-3 bg-[#070d1c] text-white">
            <div className="flex items-center gap-2.5">
              <div className="w-8 h-8 rounded-lg bg-amber-400 text-[#070d1c] flex items-center justify-center flex-shrink-0 ring-1 ring-amber-300/40">
                <Sparkles className="h-4 w-4" />
              </div>
              <div>
                <p className="font-manrope font-semibold text-sm leading-tight">PropVeiw Assistant</p>
                <p className="text-[10px] text-amber-200 uppercase tracking-wider">Claude Sonnet 4.5</p>
              </div>
            </div>
            <button
              type="button"
              onClick={() => setOpen(false)}
              className="p-1 rounded hover:bg-white/10 transition-colors"
              aria-label="Close"
              data-testid="ai-chat-close-btn"
            >
              <X className="h-4 w-4" />
            </button>
          </div>

          {/* Messages */}
          <div
            ref={scrollRef}
            className="flex-1 overflow-y-auto p-4 space-y-3 bg-slate-50/50"
            data-testid="ai-chat-messages"
          >
            {messages.map((m) => (
              <MessageBubble key={m.id} role={m.role} content={m.content} />
            ))}
            {sending && (
              <div className="flex justify-start" data-testid="ai-chat-thinking">
                <div className="bg-slate-100 text-slate-500 rounded-2xl rounded-bl-sm px-4 py-2.5 text-sm flex items-center gap-2">
                  <Loader2 className="h-3.5 w-3.5 animate-spin" />
                  Thinking…
                </div>
              </div>
            )}

            {showSuggestions && !sending && (
              <div className="pt-2 space-y-1.5">
                <p className="text-[10px] text-slate-500 uppercase tracking-wider font-medium px-1">
                  Try asking
                </p>
                {suggestions.map((s) => (
                  <button
                    key={s}
                    type="button"
                    onClick={() => send(s)}
                    className="w-full text-left text-xs px-3 py-2 rounded-lg bg-white border border-slate-200 text-slate-700 hover:border-amber-300 hover:bg-amber-50/50 transition-colors"
                    data-testid={`ai-chat-suggestion-${s.slice(0, 20).replace(/\s/g, '-')}`}
                  >
                    {s}
                  </button>
                ))}
              </div>
            )}
          </div>

          {/* Input */}
          <div className="border-t border-slate-200 p-3 bg-white">
            <div className="flex items-end gap-2">
              <textarea
                ref={inputRef}
                value={input}
                onChange={(e) => setInput(e.target.value)}
                onKeyDown={handleKeyDown}
                rows={1}
                maxLength={4000}
                placeholder="Ask about your bookings, tasks, or how to use PropVeiw…"
                className="flex-1 resize-none rounded-lg border border-slate-200 px-3 py-2 text-sm text-slate-800 placeholder:text-slate-400 focus:outline-none focus:ring-2 focus:ring-amber-300/50 focus:border-amber-300 max-h-32"
                data-testid="ai-chat-input"
              />
              <button
                type="button"
                onClick={() => send()}
                disabled={!input.trim() || sending}
                className="h-9 w-9 rounded-lg bg-amber-400 hover:bg-amber-300 disabled:bg-slate-200 disabled:text-slate-400 text-[#070d1c] flex items-center justify-center transition-colors flex-shrink-0"
                aria-label="Send"
                data-testid="ai-chat-send-btn"
              >
                <Send className="h-4 w-4" />
              </button>
            </div>
            <p className="text-[10px] text-slate-400 mt-1.5 text-center">
              Enter to send · Shift+Enter for newline
            </p>
          </div>
        </div>
      )}
    </>
  );
};

export default AIChatWidget;
